import { useAnalytics } from '~/app/features/Gallery/hooks/useAnalytics'
import { galleryImpressionsTrackingSelector } from '~/app/store/analytics'
import { instrumenter } from '~/app/utils/instrumentation'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { isPreviouslyOrderedTemplate } from '~/shared/sets/previouslyOrderedTemplate'
import { AnalyticsNames, ANALYTICS_CATEGORY, ANALYTICS_EVENT_ACTIONS, ANALYTICS_EVENT_DETAILS, ANALYTICS_LABEL, DESIGN_CREATION_TYPE } from 'shared/constants'
import { getMpvid } from '~/app/store/config'
import { getProductOptionsHash } from '~/app/utils/getProductOptionsHash'
import type { IAnalytics, GalleryImpressions, DesignAnalyticsObject, AnalyticsEntity } from 'shared/analytics/interface'
import type { GalleryContentQuery } from 'shared/types/gallery/content-query'
import type { GalleryQuickView } from 'shared/types/gallery/quick-view'
import type { State } from 'shared/types/gallery/state'
import type { PCT } from 'shared/types/vp/pct'

export type QuickViewAnalyticsBuilderParameters = {
  analytics: IAnalytics
  impressionProperties: GalleryImpressions
  mpvId: string
}

export type QuickViewAnalytics = {
  recordChangeColorSwatch: (currentEntity: GalleryQuickView.QuickViewCurrentDesignData, newDesignId: string, impressionId: string, eventDetail?: string) => void
  recordChangeFilter: (
    currentEntity: GalleryQuickView.QuickViewCurrentDesignData,
    optionName: string,
    oldOptionValue: string,
    selectedValue: string,
    quantity: number | undefined,
    impressionId: string,
    eventDetail?: string,
  ) => void
  recordSelectDesign: (
    currentEntity: GalleryQuickView.QuickViewCurrentDesignData,
    impressionId: string,
    startingProductOptions: GalleryContentQuery.ProductOptions,
    quickViewFilters: string[],
    engagementId?: string,
    initialEstimatedPriceData?: PCT.Models.ProductCatalogPricingService.QuantitiesPricingResult | null,
    currentEstimatedPriceData?: PCT.Models.ProductCatalogPricingService.QuantitiesPricingResult | null,
    startingQuantity?: number,
    currentQuantity?: number,
    eventDetail?: string,
  ) => void
  recordCloseQuickView: () => void
  recordShippingNotIncluded: () => void
  recordFirstInputFieldTouched: (fieldPurpose: string) => void
  recordOpenReviewPage: () => void
  recordAddToCart: () => void
  recordImpression: (currentEntity: GalleryQuickView.QuickViewCurrentDesignData, impressionId: string) => void
}

function createQuickviewAnalyticsData(tileEntity: GalleryQuickView.QuickViewCurrentDesignData): DesignAnalyticsObject {
  return {
    tileEntity,
    location: AnalyticsNames.Quickview,
  }
}

function trackImpression(
  currentEntity: GalleryQuickView.QuickViewCurrentDesignData,
  analytics: IAnalytics,
  impressionProperties: GalleryImpressions,
  impressionId: string,
  mpvId: string,
): void {
  const analyticsData = createQuickviewAnalyticsData(currentEntity)

  const isDirectQuickView = currentEntity.isQSPEntity
  const { rankingSet, designTypeMetadata } = currentEntity
  const { conceptKitId, useCaseId, layoutId } = designTypeMetadata || {}

  const dataEntity = {
    productKey: currentEntity.productKey,
    product: currentEntity.product,
    designId: currentEntity.currentEntityId,
    colorSwatchValues: currentEntity.colorSwatches.map((cs) => cs.color?.split('#')[1]).filter(Boolean),
    isForceRanked: currentEntity.isForceRanked,
    galleryImpressionId: impressionId,
    productOptionsSetId: 1,
    designConceptId: currentEntity.designConceptId,
    externalId: currentEntity.currentColorSwatch.externalId,
    targetUseCase: currentEntity.targetUseCase,
    mpvId,
    designCreationType: currentEntity.designCreationType,
    rankingSet: currentEntity.rankingSet,
    isPreviouslyOrdered: isPreviouslyOrderedTemplate(rankingSet),
    conceptKitId,
    useCaseId,
    layoutId,
  } as AnalyticsEntity

  if (!isDirectQuickView) {
    dataEntity.position = analyticsData.tileEntity.position
  }

  const productOptionsSet = {
    id: 1,
    options: currentEntity.fullProductOptions,
  }

  analytics.trackImpression({
    ...impressionProperties,
    isDirectQuickView,
    entities: [dataEntity],
    productOptionsSets: [productOptionsSet],
  })
}

const buildQuickViewAnalytics = ({ analytics, impressionProperties, mpvId }: QuickViewAnalyticsBuilderParameters): QuickViewAnalytics => ({
  recordChangeColorSwatch: (
    currentEntity: GalleryQuickView.QuickViewCurrentDesignData,
    newDesignId: string,
    impressionId: string,
    eventDetail?: string,
  ): void => {
    const analyticsData = createQuickviewAnalyticsData(currentEntity)
    const color = currentEntity.colorSwatches.find((cs) => cs.designId === newDesignId)?.color?.split('#')[1]

    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.GALLERY_DESIGN_ENGAGEMENT,
      eventLabel: ANALYTICS_LABEL.SELECT_COLOR_SWATCH,
      eventDetail: eventDetail || '',
      ...analytics.buildDesignEngagement({
        engagementAction: ANALYTICS_LABEL.SELECT_COLOR_SWATCH,
        selectedDesign: newDesignId,
        color,
        tileEntity: analyticsData.tileEntity,
        colorSwatchObjects: currentEntity.colorSwatches,
        impressionId,
        location: analyticsData.location,
        position: analyticsData.tileEntity.position,
        selectedQuickViewOptions: currentEntity.fullProductOptions,
      }),
    })

    analytics.trackAttributeSelected('Design Substrate', color)
  },
  recordChangeFilter: (
    currentEntity: GalleryQuickView.QuickViewCurrentDesignData,
    optionName: string,
    oldOptionValue: string,
    selectedValue: string,
    quantity: number | undefined,
    impressionId: string,
    eventDetail?: string,
  ): void => {
    trackImpression(currentEntity, analytics, impressionProperties, impressionId, mpvId)

    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.GALLERY_UPSELL_ENGAGEMENT,
      eventLabel: ANALYTICS_EVENT_ACTIONS.GALLERY_UPSELL_ENGAGEMENT,
      quantity,
      eventDetail: eventDetail || '',
      ...analytics.buildUpsellEngagement({
        selectedDesign: currentEntity.designId,
        selectedDesignId: currentEntity.currentEntityId,
        productKey: currentEntity.productKey,
        productOptions: currentEntity.fullProductOptions,
        optionName,
        oldOptionValue,
        newOptionValue: selectedValue,
        designCreationType: currentEntity.designCreationType,
        color:
          currentEntity.designCreationType === DESIGN_CREATION_TYPE.DYNAMIC
            ? currentEntity.currentColorSwatch.designId.split('__')[1]
            : currentEntity.currentColorSwatch.color?.split('#')[1],
        designConceptId: currentEntity.designConceptId,
        externalId: currentEntity.currentColorSwatch.externalId,
        targetUseCase: currentEntity.targetUseCase,
        designTypeMetadata: currentEntity.designTypeMetadata,
      }),
    })

    analytics.trackAttributeSelected(optionName, selectedValue)
  },
  recordSelectDesign: (
    currentEntity: GalleryQuickView.QuickViewCurrentDesignData,
    impressionId: string,
    startingProductOptions: GalleryContentQuery.ProductOptions,
    quickViewFilters: string[],
    engagementId?: string,
    initialEstimatedPriceData?: PCT.Models.ProductCatalogPricingService.QuantitiesPricingResult | null,
    currentEstimatedPriceData?: PCT.Models.ProductCatalogPricingService.QuantitiesPricingResult | null,
    startingQuantity?: number,
    currentQuantity?: number,
    eventDetail?: string,
  ): void => {
    instrumenter.recordDesignSelection({ designId: currentEntity.currentDesignId }, 'tile')
    const analyticsData = createQuickviewAnalyticsData(currentEntity)
    const color = currentEntity.currentColorSwatch.color?.split('#')[1]

    const didQuickViewOptionsChange = getProductOptionsHash(currentEntity.productOptions) !== getProductOptionsHash(startingProductOptions)

    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.GALLERY_DESIGN_ENGAGEMENT,
      eventLabel: ANALYTICS_LABEL.SELECT_DESIGN,
      eventDetail: eventDetail || '',
      ...analytics.buildDesignEngagement({
        engagementAction: ANALYTICS_LABEL.SELECT_DESIGN,
        selectedDesign: currentEntity.currentDesignId,
        color,
        tileEntity: analyticsData.tileEntity,
        colorSwatchObjects: currentEntity.colorSwatches,
        impressionId,
        location: analyticsData.location,
        position: analyticsData.tileEntity.position,
        selectedQuickViewOptions: currentEntity.fullProductOptions,
        didQuickViewOptionsChange: !!didQuickViewOptionsChange,
        engagementId,
      }),
    })

    analytics.trackProductOptionsSelected({
      currentEntity,
      quickViewFilters,
      startingProductOptions,
      initialEstimatedPriceData,
      currentEstimatedPriceData,
      startingQuantity,
      currentQuantity,
    })
  },
  recordCloseQuickView: (): void => {
    analytics.trackEvent({
      category: ANALYTICS_CATEGORY.FLY_OUT,
      action: ANALYTICS_EVENT_ACTIONS.FLY_OUT_CLOSED,
      eventLabel: ANALYTICS_LABEL.FLY_OUT_EVENT,
    })
  },
  recordShippingNotIncluded: (): void => {
    analytics.trackEvent({
      category: ANALYTICS_CATEGORY.FLY_OUT,
      action: ANALYTICS_EVENT_ACTIONS.FLY_OUT_CLICKED,
      eventLabel: ANALYTICS_LABEL.FLY_OUT_EVENT,
      eventDetail: ANALYTICS_EVENT_DETAILS.SHIPPING_NOT_INCLUDED,
    })
  },
  recordFirstInputFieldTouched: (fieldPurpose: string): void => {
    analytics.trackEvent({
      category: ANALYTICS_CATEGORY.FLY_OUT,
      action: ANALYTICS_EVENT_ACTIONS.FLY_OUT_CLICKED,
      eventLabel: ANALYTICS_LABEL.FLY_OUT_EVENT,
      eventDetail: `${fieldPurpose} Text Clicked`,
    })
  },
  recordOpenReviewPage: (): void => {
    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.FLY_OUT_VIEWED,
      category: ANALYTICS_CATEGORY.FLY_OUT,
      eventLabel: ANALYTICS_LABEL.REVIEW_FLY_OUT_EVENT,
      eventDetail: ANALYTICS_EVENT_DETAILS.REVIEW,
    })
  },
  recordAddToCart: (): void => {
    analytics.trackEvent({
      action: ANALYTICS_EVENT_ACTIONS.FLY_OUT_CLICKED,
      category: ANALYTICS_CATEGORY.FLY_OUT,
      eventLabel: ANALYTICS_LABEL.REVIEW_FLY_OUT_EVENT,
      eventDetail: ANALYTICS_LABEL.ADD_TO_CART,
    })
  },
  recordImpression: (currentEntity: GalleryQuickView.QuickViewCurrentDesignData, impressionId: string): void => {
    trackImpression(currentEntity, analytics, impressionProperties, impressionId, mpvId)
  },
})

export const useQuickViewAnalytics = (): QuickViewAnalytics => {
  const analytics = useAnalytics()
  const mpvId = useSelector(getMpvid)
  const impressionProperties = useSelector((state: State.GlobalState) => galleryImpressionsTrackingSelector(state, analytics, AnalyticsNames.Quickview))

  const memo = useMemo(
    () =>
      buildQuickViewAnalytics({
        analytics,
        impressionProperties,
        mpvId,
      }),
    [analytics, impressionProperties, mpvId],
  )

  return memo
}
